import { Component, Input, Output, EventEmitter, Inject } from '@angular/core';
import { combineLatest, forkJoin, Observable, of } from "rxjs";

import { mergeMap, map } from "rxjs/operators";

import { TraductionPaEntity } from '../../entities/traduction-pa.entity';
import { DocumentPersonneAvertie, CategoriePa, EtatDocumentPa } from '../../entities/document-pa.entity';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { ButtonActions } from '../../../../app.constants';
import { StepAction } from "../../entities/step-action-entity";
import { DocumentPaButton } from '../../entities/document-pa-button';
import { DocumentPaTraductionUpdateOrder, ResponsabiliteDocument } from '../../entities/document-pa-traduction-update-order.entity';

import { DocumentPaService } from "../../services/document-pa-service";
import { GenericUpdateTraductionStepOrder } from '../../entities/document-pa-generic-update-traduction-step-order.entity';

import { DocumentPaAutreMesurePrevention } from '../../entities/document-pa-autre-mesure-prevention.entity';

export interface GroupeRisquePa {
    key: string;
    facteurs: TraductionPaEntity[];
    mesures: TraductionPaEntity[];
}

@Component({
    selector: 'analyse-risque-generique-pa',
    templateUrl: 'analyse-risque-generique-pa.html',
    styleUrls: ['analyse-risque-generique-pa.css']
})
export class AnalyseRisquesGeneriquePaComponent {

    @Output() actionRequested = new EventEmitter<StepAction>();

    currentPaInstance!: DocumentPersonneAvertie;
    ResponsabiliteDocument = ResponsabiliteDocument;
    traductions: TraductionPaEntity[] = [];
    highlightedSections: number[] = [];
    label1 = "Facteurs de risque associés";
    label2 = "Mesures de prévention et de protection";

    risqueSpecifique: TraductionPaEntity[] = [];

    paEtats: typeof EtatDocumentPa = EtatDocumentPa;

    actionButtons: DocumentPaButton[] = [
        new DocumentPaButton(ButtonActions.PREVIOUS, 'Back', 'btn btn-primary'),
        new DocumentPaButton(ButtonActions.CANCEL, 'Liste des PA', 'btn btn-danger'),
        new DocumentPaButton(ButtonActions.SAVE, 'Sauvegarder maintenant', 'btn btn-warning'),
        new DocumentPaButton(ButtonActions.NEXT, 'Next', 'btn btn-success'),
    ];

    private currentPa$ = new SubjectRelay<DocumentPersonneAvertie>();
    @Input() set currentPa(obs: Observable<DocumentPersonneAvertie> | undefined) {
        this.currentPa$.registerSource(obs);
    }

    @Input() type: string = 'generique';

    constructor(@Inject(DocumentPaService) private documentPaService: DocumentPaService) {
        this.currentPa$.subscribe(
            (currentPa) => {
                if (currentPa) {
                    this.currentPaInstance = new DocumentPersonneAvertie();
                    this.currentPaInstance.id = currentPa.id;
                    this.currentPaInstance.duplicatedId = currentPa.duplicatedId;
                    this.currentPaInstance.etat = currentPa.etat;
                    this.currentPaInstance.lastCompletedStep = currentPa.lastCompletedStep;
                    this.currentPaInstance.categorie = currentPa.categorie;
                    if (this.currentPaInstance.categorie) {
                        let risqueSpe$ = this.documentPaService.getTraductions(
                            this.currentPaInstance.categorie,
                            this.type === 'specifique' ? 'PARENT_RISQUE_SPE' : 'PARENT_RISQUE_GLO'
                        );
                        const sourceId = this.currentPaInstance.duplicatedId
                            ? this.currentPaInstance.duplicatedId
                            : this.currentPaInstance.id;

							const stepData$ =
							    this.type === 'specifique'
							        ? this.documentPaService.getAnalyseSpecifiqueData(sourceId)
							        : this.documentPaService.getAnalyseGlobaleData(sourceId);

							const autreMps$ =
							    this.type === 'specifique'
							        ? this.documentPaService.getAutreMpsAnalyseSpecifique(sourceId)
							        : this.documentPaService.getAutreMpsAnalyseGlobale(sourceId);

							const data$ = combineLatest([risqueSpe$, stepData$, autreMps$]).pipe(
							    map(([risqueSpe, stepData, autreMps]) => ({ risqueSpe, stepData, autreMps }))
							);

							data$.subscribe((data) => {
							    this.risqueSpecifique = data.risqueSpe as TraductionPaEntity[];

							    if (data.stepData && data.stepData.traductions) {
							        const checkedTrads = data.stepData.traductions.filter($ => $.checked).map(ct => ct.traductionId);

							        this.risqueSpecifique.forEach(trad =>
							            trad.checked = checkedTrads.includes(trad.id)
							        );

							        this.risqueSpecifique.forEach(parent => {
							            (parent.enfants || []).forEach(child => {
							                child.checked = checkedTrads.includes(child.id) || (child.checked ?? false);
							                const stepDataTrad = data.stepData.traductions.find(t => t.traductionId === child.id);
							                if (stepDataTrad && stepDataTrad.responsibility) {
							                    child.responsibility = stepDataTrad.responsibility;
							                }
							            });
							        });
							    }

							    this.traductions = this.organizeTraductions(this.risqueSpecifique);
							    this.applyAutreMesures(data.autreMps || []);
							    this.traductions.forEach(parent => this.updatePanelHighlight(parent));
							    this.validateNextButton();
							});

                    }
                }
            }
        );
    }

	private organizeTraductions(data: TraductionPaEntity[]): TraductionPaEntity[] {
		    return data.map(parent => {

		        const activites = (parent.enfants || [])
		            .filter(e => e.type === "NATURE_RISQUE_" + (this.type === 'specifique' ? 'SPE' : 'GLO'))
		            .map(x => ({ ...x, checked: x.checked ?? false })) as TraductionPaEntity[];

		        const riskOrImpacts = (parent.enfants || [])
		            .filter(e => e.type === "FACTEUR_RISQUE_" + (this.type === 'specifique' ? 'SPE' : 'GLO'))
		            .map(x => ({ ...x, checked: x.checked ?? false })) as TraductionPaEntity[];

		        const mesures = (parent.enfants || [])
		            .filter(e => e.type === "MESURE_PREVENTION_" + (this.type === 'specifique' ? 'SPE' : 'GLO'))
		            .map(x => ({
		                ...x,
		                checked: x.checked ?? false,
		                responsibility: x.responsibility ?? null
		            })) as TraductionPaEntity[];

		        this.markGroupEnds(activites);
		        this.ajouterMesurePrevention(mesures);
				this.ajouterFacteurAutre(riskOrImpacts);

		        return {
		            ...parent,
		            checked: parent.checked ?? false,
		            activites,
		            riskOrImpacts,
		            mesures,
		            groupes: this.buildGroupes(riskOrImpacts, mesures)
		        };
		    });
		}

		private buildGroupes(facteurs: TraductionPaEntity[], mesures: TraductionPaEntity[]): GroupeRisquePa[] {
		    const keys: string[] = [];

		    [...(facteurs || []), ...(mesures || [])].forEach(x => {
		        const k = this.groupKey(x);
		        if (!keys.includes(k)) keys.push(k);
		    });

		    keys.sort((a, b) => {
		        if (a === 'AUTRE') return 1;
		        if (b === 'AUTRE') return -1;
		        return Number(a) - Number(b);
		    });

		    return keys.map(k => ({
		        key: k,
		        facteurs: (facteurs || []).filter(f => this.groupKey(f) === k),
		        mesures: (mesures || []).filter(m => this.groupKey(m) === k)
		    }));
		}

		private refreshGroupes(parent: TraductionPaEntity): void {
		    parent.groupes = this.buildGroupes(parent.riskOrImpacts, parent.mesures);
		}

		private groupKey(item: TraductionPaEntity): string {
		    if (this.isAutre(item)) return 'AUTRE';
		    return String(item.infoGroupement ?? '');
		}

		private markGroupEnds(list: TraductionPaEntity[]): void {
		    (list || []).forEach((item, i) => {
		        const next = list[i + 1];
		        item.isGroupEnd = !!next && this.groupKey(item) !== this.groupKey(next);
		    });
		}

	isAutre(m: TraductionPaEntity): boolean {
	    return m.presentation === 'textarea';
	}
	
	private isMesureComplete(m: TraductionPaEntity): boolean {
	    if (!m.checked) return true;

	    const respOk =
	        m.responsibility === ResponsabiliteDocument.EU ||
	        m.responsibility === ResponsabiliteDocument.EE ||
	        m.responsibility === ResponsabiliteDocument.BOTH;

	    const texteOk = !this.isAutre(m) || !!(m.texteLibre && m.texteLibre.trim());

	    return respOk && texteOk;
	}
	
	private hasGroupeIncomplet(parent: TraductionPaEntity): boolean {
	    return (parent.groupes || []).some((g: GroupeRisquePa) =>
	        g.facteurs.length > 0 &&
	        g.facteurs.some((f: TraductionPaEntity) => f.checked) &&
	        !g.mesures.some((m: TraductionPaEntity) => m.checked)
	    );
	}
	
	private applyAutreMesures(autres: DocumentPaAutreMesurePrevention[]): void {
	    this.traductions.forEach(parent => {

	        const saved = (autres || [])
	            .filter(a => a.parentId === parent.id && !!(a.autre || '').trim());

	        parent.mesures = parent.mesures.filter(m => !this.isAutre(m));

	        saved.forEach(s => {
	            const autreMp = new TraductionPaEntity();
	            autreMp.fr = 'Autre';
	            autreMp.presentation = 'textarea';
	            autreMp.checked = s.checked ?? true;
	            autreMp.texteLibre = s.autre;
	            autreMp.responsibility = s.responsabilite ?? null;
	            autreMp.autreId = s.id;
	            parent.mesures.push(autreMp);
	        });

	        this.ajouterMesurePrevention(parent.mesures); 
			this.ajouterFacteurAutre(parent.riskOrImpacts);   
			this.syncFacteurAutre(parent); 
	        this.refreshGroupes(parent);
	    });
	}
	
	private ajouterMesurePrevention(mesures: TraductionPaEntity[]): void {
	    const vierges = mesures.filter(m => this.isAutre(m) && !m.checked);

	    vierges.slice(1).forEach(v => {
	        const idx = mesures.indexOf(v);
	        if (idx > -1) mesures.splice(idx, 1);
	    });

	    if (!vierges.length) {
	        const autreMp = new TraductionPaEntity();
	        autreMp.fr = 'Autre';
	        autreMp.presentation = 'textarea';
	        autreMp.checked = false;
	        autreMp.texteLibre = '';
	        autreMp.responsibility = null;
	        autreMp.autreId = null;
	        mesures.push(autreMp);
	    }

	    this.markGroupEnds(mesures);
	}

    onChangeChargeEU(child: TraductionPaEntity, parent: TraductionPaEntity) {
        if (!child.checked) return;

        if (child.responsibility === ResponsabiliteDocument.EU) {
            child.responsibility = null;
        }
        else if (child.responsibility === ResponsabiliteDocument.BOTH) {
            child.responsibility = ResponsabiliteDocument.EE;
        }
        else if (child.responsibility === ResponsabiliteDocument.EE) {
            child.responsibility = ResponsabiliteDocument.BOTH;
        }
        else {
            child.responsibility = ResponsabiliteDocument.EU;
        }

        this.updatePanelHighlight(parent);
        this.validateNextButton();
    }

    onChangeChargeEE(child: TraductionPaEntity, parent: TraductionPaEntity) {
        if (!child.checked) return;

        if (child.responsibility === ResponsabiliteDocument.EE) {
            child.responsibility = null;
        }
        else if (child.responsibility === ResponsabiliteDocument.BOTH) {
            child.responsibility = ResponsabiliteDocument.EU;
        }
        else if (child.responsibility === ResponsabiliteDocument.EU) {
            child.responsibility = ResponsabiliteDocument.BOTH;
        }
        else {
            child.responsibility = ResponsabiliteDocument.EE;
        }

        this.updatePanelHighlight(parent);
        this.validateNextButton();
    }

    noneSelected(list: any[]) {
        return !list || !list.some(item => item.checked);
    }

    performAction(requestedAction: ButtonActions): void {
        console.log('requested action:', requestedAction);

        switch (requestedAction) {

            case ButtonActions.CANCEL:
            case ButtonActions.RETURN_TO_LIST:
                this.actionRequested.emit(
                    new StepAction(null, ButtonActions.RETURN_TO_LIST)
                );
                break;

            case ButtonActions.PREVIOUS:
                this.actionRequested.emit(
                    new StepAction(this.currentPaInstance, ButtonActions.PREVIOUS)
                );
                break;

            case ButtonActions.SAVE:
            case ButtonActions.SAVE_DRAFT:
                this.saveAnalyse(ButtonActions.RETURN_TO_LIST);
                break;

            case ButtonActions.NEXT:
                this.saveAnalyse(ButtonActions.NEXT);
                break;

            default:
                console.warn('Unhandled action:', requestedAction);
        }
    }

	onChangeActivite(parent: TraductionPaEntity) {
	    const anySelected = parent.activites.some(a => a.checked);

	    if (!anySelected) {
	        parent.riskOrImpacts.forEach(r => r.checked = false);

	        parent.mesures.forEach(m => {
	            m.checked = false;
	            m.responsibility = null;
	            if (this.isAutre(m)) {
	                m.texteLibre = '';
	                m.autreId = null;      
	            }
	        });

	        this.ajouterMesurePrevention(parent.mesures);  
	        this.syncFacteurAutre(parent);                 
	        this.refreshGroupes(parent);
	    }

	    this.updatePanelHighlight(parent);
	    this.validateNextButton();
	}

	onChangeRiskOrImpact(parent: TraductionPaEntity) {
	    (parent.groupes || []).forEach((g: GroupeRisquePa) => {
	        const pilote = g.facteurs.length ? g.facteurs : parent.riskOrImpacts;
	        if (pilote.some((f: TraductionPaEntity) => f.checked)) return;

	        g.mesures.forEach((m: TraductionPaEntity) => {
	            m.checked = false;
	            m.responsibility = null;
	            if (this.isAutre(m)) m.texteLibre = '';
	        });
	    });

		this.ajouterMesurePrevention(parent.mesures);   
		this.refreshGroupes(parent);
	    this.updatePanelHighlight(parent);
	    this.validateNextButton();
	}
	
	isGroupeDisabled(g: GroupeRisquePa, parent: TraductionPaEntity): boolean {
	    return g.facteurs.length
	        ? this.noneSelected(g.facteurs)
	        : this.noneSelected(parent.riskOrImpacts);
	}
	
	onChangeMesurePrevention(child: TraductionPaEntity, parent: TraductionPaEntity) {
	    if (!child.checked) {
	        child.responsibility = null;
	        if (this.isAutre(child)) child.texteLibre = '';
	    }
	    else if (!child.responsibility && !this.isAutre(child)) {
	        child.responsibility = this.getPreselectedResponsibility(child);
	    }

	    this.updatePanelHighlight(parent);
	    this.validateNextButton();
	    this.ajouterMesurePrevention(parent.mesures);
	    this.refreshGroupes(parent);
	}

	onChangeTexteAutre(parent: TraductionPaEntity) {
	    this.updatePanelHighlight(parent);
	    this.validateNextButton();
	}

	private getPreselectedResponsibility(child: TraductionPaEntity): ResponsabiliteDocument | null {
	    const fields = (child.customFields || '')
	        .split(',')
	        .map((f: string) => f.trim());

	    const preselectEu = fields.includes('shouldPreselectEu');
	    const preselectEe = fields.includes('shouldPreselectEe');

	    if (preselectEu && preselectEe) return ResponsabiliteDocument.BOTH;
	    if (preselectEu) return ResponsabiliteDocument.EU;
	    if (preselectEe) return ResponsabiliteDocument.EE;

	    return null;
	}
	
	private ajouterFacteurAutre(facteurs: TraductionPaEntity[]): void {
	    if ((facteurs || []).some(f => this.isAutre(f))) return;

	    const autreFacteur = new TraductionPaEntity();
	    autreFacteur.fr = 'Autre (préciser le risque dans la case Mesures)';
	    autreFacteur.presentation = 'textarea';  
	    autreFacteur.checked = false;
	    autreFacteur.autreId = null;
	    facteurs.push(autreFacteur);
	}

	private syncFacteurAutre(parent: TraductionPaEntity): void {
	    const facteurAutre = (parent.riskOrImpacts || []).find(f => this.isAutre(f));
	    if (!facteurAutre) return;

	    facteurAutre.checked = (parent.mesures || [])
	        .some(m => this.isAutre(m) && m.checked);
	}

    buildPayload(): GenericUpdateTraductionStepOrder {
        const payload: GenericUpdateTraductionStepOrder = new GenericUpdateTraductionStepOrder();
        payload.id = this.currentPaInstance?.id;
        payload.traductions = [];

        this.traductions.forEach(parent => {

            if (parent.checked) {
                payload.traductions.push({
                    traductionId: parent.id,
                    checked: true
                } as DocumentPaTraductionUpdateOrder);
            }

            // ACTIVITÉS
            parent.activites?.forEach(a => {
                if (a.checked) {
                    payload.traductions.push({
                        traductionId: a.id,
                        checked: true
                    } as DocumentPaTraductionUpdateOrder);
                }
            });

            // RISQUES 
            parent.riskOrImpacts?.forEach(r => {
				if (this.isAutre(r)) return; 
                if (r.checked) {
                    payload.traductions.push({
                        traductionId: r.id,
                        checked: true
                    } as DocumentPaTraductionUpdateOrder);
                }
            });

            // MESURES 
			parent.mesures?.forEach(m => {
			    if (!m.checked) return;

			    if (this.isAutre(m)) {
			        const texte = (m.texteLibre || '').trim();
			        if (!texte) return;

			        payload.traductions.push({
			            traductionId: parent.id,
			            checked: true,
			            responsibility: m.responsibility,
			            autre: texte,
			            customFields: 'autre'
			        } as DocumentPaTraductionUpdateOrder);
			    } else {
			        payload.traductions.push({
			            traductionId: m.id,
			            checked: true,
			            responsibility: m.responsibility
			        } as DocumentPaTraductionUpdateOrder);
			    }
			});
        });

        return payload;
    }

    validateNextButton() {

        let next = true;

        const anythingSelected = this.traductions.some(parent =>
            parent.activites.some(a => a.checked) ||
            parent.riskOrImpacts.some(r => r.checked) ||
            parent.mesures.some(m => m.checked)
        );

        if (!anythingSelected) {
            this.actionButtons[2].disabled = true;
            this.actionButtons[3].disabled = true;
            return;
        }

        for (const parent of this.traductions) {

            const act = parent.activites.some(a => a.checked);
            if (!act) continue;

			const risk = parent.riskOrImpacts.some(r => r.checked);
            const meas = parent.mesures.some(m => m.checked);
            const respOk = parent.mesures.every(m => this.isMesureComplete(m));

            if (!risk || !meas || !respOk || this.hasGroupeIncomplet(parent)) {   // <-- ici
                next = false;
                break;
            }
        }

        this.actionButtons[2].disabled = !next;
        this.actionButtons[3].disabled = !next;
    }

    updatePanelHighlight(parent: TraductionPaEntity) {

        const actSelected = parent.activites.some(a => a.checked);
        const riskSelected = parent.riskOrImpacts.some(r => r.checked);
        const mesureSelected = parent.mesures.some(m => m.checked);

        parent.checked = actSelected || riskSelected || mesureSelected;
		const allMeasuresValid = parent.mesures.every(m => this.isMesureComplete(m));
			
        if (!actSelected && !riskSelected && !mesureSelected) {
            this.highlightedSections = this.highlightedSections.filter(id => id !== parent.id);
            return;
        }
			
		const isIncomplete = !actSelected || !riskSelected || !mesureSelected || !allMeasuresValid || this.hasGroupeIncomplet(parent);  

        if (isIncomplete) {
            if (!this.highlightedSections.includes(parent.id)) {
                this.highlightedSections.push(parent.id);
            }
            return;
        }

        this.highlightedSections = this.highlightedSections.filter(id => id !== parent.id);
    }

    private saveAnalyse(afterSaveAction: ButtonActions): void {

        const payload = this.buildPayload();

        const save$ =
            this.type === 'specifique'
                ? this.documentPaService.updateAnalyseSpecifiqueStep(payload)
                : this.documentPaService.updateAnalyseGlobalStep(payload);

        save$.subscribe({
            next: (response) => {
                if (this.currentPaInstance) {
                    const stepNumber = this.type === 'specifique' ? 5 : 7;
                    if (this.currentPaInstance.lastCompletedStep < stepNumber) {
                        this.currentPaInstance.lastCompletedStep = stepNumber;
                    }
                    this.currentPaInstance.id = response.id;
                }

                this.actionRequested.emit(
                    new StepAction(this.currentPaInstance, afterSaveAction)
                );
            },
            error: (err) => {
                console.error(
                    "Erreur lors de la sauvegarde des informations du step analyse",
                    this.type,
                    err
                );
            }
        });
    }

}
